import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { GraduationCap, Trophy, Calendar as CalIcon, NotebookPen } from "lucide-react";

const GRADE_TIER = (g) => {
  if (!g || g === "—" || g === "INC") return { color: "#8a9099", bg: "bg-titans-elev", border: "border-titans-silver/30", label: "—" };
  if (g.startsWith("A")) return { color: "#4ade80", bg: "bg-green-500/15", border: "border-green-500/60" };
  if (g.startsWith("B")) return { color: "#4B92DB", bg: "bg-titans-blue/15", border: "border-titans-blue/60" };
  if (g.startsWith("C")) return { color: "#f5c518", bg: "bg-yellow-500/15", border: "border-yellow-500/60" };
  if (g.startsWith("D")) return { color: "#f97316", bg: "bg-orange-500/15", border: "border-orange-500/60" };
  if (g === "F")         return { color: "#C8102E", bg: "bg-titans-red/20",  border: "border-titans-red" };
  return { color: "#8a9099", bg: "bg-titans-elev", border: "border-titans-silver/30" };
};

export default function RookieReportCards() {
  const [rookies, setRookies] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/rookies");
      setRookies(data.rookies || []);
    } catch (e) {
      console.error("Rookies load failed:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const stats = useMemo(() => {
    const totalGrades = rookies.reduce((acc, r) => acc + (r.season_grades?.length || 0), 0);
    const graded = rookies.filter((r) => (r.season_grades?.length || 0) > 0).length;
    return { totalGrades, graded };
  }, [rookies]);

  if (loading) return <div className="text-titans-silver">Loading rookie report cards…</div>;
  if (!rookies.length) return null;

  return (
    <div data-testid="rookie-report-cards">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="font-cond uppercase tracking-[0.3em] text-xs text-titans-blue">Class of 2026</div>
          <h2 className="font-display text-3xl md:text-4xl uppercase tracking-wide section-title flex items-center gap-2">
            <GraduationCap size={24} className="text-titans-red" /> Rookie Report Cards
          </h2>
          <p className="text-titans-silver text-sm font-body max-w-xl mt-2">
            Weekly grades tracked by Two-Tone Blue Nation. Stats, snap counts, and short notes per game.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="titans-card p-3 min-w-[120px]">
            <div className="font-cond uppercase tracking-wider text-[10px] text-titans-silver">Class Size</div>
            <div className="font-display text-2xl">{rookies.length}</div>
          </div>
          <div className="titans-card p-3 min-w-[120px]">
            <div className="font-cond uppercase tracking-wider text-[10px] text-titans-silver">Weeks Graded</div>
            <div className="font-display text-2xl">{stats.totalGrades}</div>
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4" data-testid="rookie-grid">
        {rookies.map((r) => {
          const tier = GRADE_TIER(r.avg_grade);
          const recent = (r.season_grades || []).slice().reverse().slice(0, 1)[0];
          return (
            <div
              key={r.id}
              data-testid={`rookie-card-${r.id}`}
              className={`titans-card p-5 border-l-4 ${tier.border}`}
            >
              <div className="flex items-start gap-4">
                {/* Big grade badge */}
                <div
                  className={`flex flex-col items-center justify-center w-20 h-20 ${tier.bg} border ${tier.border} flex-shrink-0`}
                >
                  <div className="font-cond uppercase tracking-wider text-[9px] text-titans-silver">Avg</div>
                  <div className="font-display text-3xl leading-none" style={{ color: tier.color }}>
                    {r.avg_grade}
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="bg-titans-red text-white font-display text-xs px-2 py-0.5">
                      Rd {r.draft_round} · #{r.draft_pick}
                    </div>
                    <div className="font-cond uppercase tracking-wider text-[10px] text-titans-blue">
                      {r.position}
                    </div>
                  </div>
                  <div className="font-display text-xl uppercase leading-tight">{r.name}</div>
                  <div className="font-cond uppercase tracking-wider text-[10px] text-titans-silver mt-0.5">
                    {r.college}
                  </div>
                  {r.summary && (
                    <p className="text-titans-silver text-xs font-body mt-2 leading-snug">{r.summary}</p>
                  )}
                </div>
              </div>

              {/* Recent week + week-by-week strip */}
              {(r.season_grades?.length || 0) > 0 && (
                <div className="mt-4 pt-4 border-t border-[rgba(138,141,143,0.15)]">
                  {recent && (
                    <div className="mb-3 flex items-start gap-2">
                      <Trophy size={14} className="text-titans-blue mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="font-cond uppercase tracking-wider text-[10px] text-titans-silver">
                          Most Recent · Week {recent.week} · <span style={{ color: GRADE_TIER(recent.grade).color }}>{recent.grade}</span>
                          {recent.snaps != null && <span className="ml-2 text-titans-muted">· {recent.snaps} snaps</span>}
                        </div>
                        {recent.note && (
                          <div className="text-white/90 font-body text-sm mt-0.5 leading-snug">{recent.note}</div>
                        )}
                      </div>
                    </div>
                  )}
                  <div className="flex flex-wrap gap-1.5" data-testid={`rookie-weeks-${r.id}`}>
                    {r.season_grades.map((g) => {
                      const t = GRADE_TIER(g.grade);
                      return (
                        <div
                          key={g.week}
                          title={`Week ${g.week}: ${g.grade}${g.note ? ` — ${g.note}` : ""}`}
                          className={`inline-flex items-center gap-1 border ${t.border} ${t.bg} px-2 py-1`}
                        >
                          <span className="font-cond text-[9px] text-titans-silver">W{g.week}</span>
                          <span className="font-display text-xs" style={{ color: t.color }}>{g.grade}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {(r.season_grades?.length || 0) === 0 && (
                <div className="mt-4 pt-4 border-t border-[rgba(138,141,143,0.15)] flex items-center gap-2 text-titans-muted text-xs">
                  <NotebookPen size={12} /> No grades yet — check back after Sunday.
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-6 flex items-center justify-center gap-2 text-titans-silver text-[10px] font-cond uppercase tracking-wider">
        <CalIcon size={11} className="text-titans-blue" />
        Graded weekly by The Titan Standard editorial team
      </div>
    </div>
  );
}
